import { useQuery } from '@tanstack/react-query';
import { useLaserEyes } from '@omnisat/lasereyes-react';
import { mapAlkaneUtxos } from '../utils/alkanodeUtxos';
import { isMainnetBtcAddress } from '../utils/arbuzUtils';
export const ALKANES_BY_ADDRESS_KEY = 'alkanes-by-address';

const RETRY_DELAY_MS = 2000;
const DEADLINE_MS = 3 * 60 * 1000;

export function useAddressAlkanes() {
  const { address, connected } = useLaserEyes();

  return useQuery({
    queryKey: [ALKANES_BY_ADDRESS_KEY, address],
    queryFn: async () => {
      const deadline = Date.now() + DEADLINE_MS;

      // Single indexed call: alkane UTXOs with their sat values baked in
      // (get-alkanes-utxo returns txId/outputIndex + satoshis + alkanes map). This
      // replaces the old alkanode-outpoints + mempool.space-utxo pair; mempool 400s
      // past 500 UTXOs ("Too many unspent transaction outputs"), which broke the
      // whole query for heavy wallets and never surfaced their alkanes.
      while (true) {
        try {
          const res = await fetch('/api/alkanes-utxo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const items = data?.data;
          if (!Array.isArray(items)) throw new Error('alkanode error');

          return { outpoints: mapAlkaneUtxos(items) };
        } catch {
          // Surface the failure instead of returning [] - an empty result here would
          // be cached as "wallet is empty" forever (staleTime: Infinity).
          if (Date.now() >= deadline) throw new Error('err_network');
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
        }
      }
    },
    enabled: !!connected && isMainnetBtcAddress(address),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
